import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { useInitials } from "~/hooks/use-initials";
import { useCurrentCompanyStore } from "~/store/company/currentCompanyStore";
import { type SharedData } from "~/types";
import type { Data } from "@generated/data";
import { router, usePage } from "@inertiajs/react";
import { Building2, Check } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { urlFor } from "~/client";
import { Button } from "../ui/button";

type CompanyInfoMenuProps = {
  companies: Data.Company[];
};

export function CompanyInfoMenu({ companies }: CompanyInfoMenuProps) {
  const { auth } = usePage<SharedData>().props;
  const getInitials = useInitials();
  const { company: selectedCompany, setCompany } = useCurrentCompanyStore();
  const { t } = useTranslation("home_common");

  useEffect(() => {
    setCompany(auth.company);
  }, [auth.company, setCompany]);

  if (!selectedCompany) {
    return null;
  }

  const handleCompanyChange = (company: Data.Company) => {
    // Don't switch if already on the selected company
    if (Number(selectedCompany?.id) === Number(company.id)) {
      return;
    }

    router.post(
      urlFor("switch-company"),
      {
        company_id: Number(company.id),
      },
      {
        onError: (errors) => {
          console.error("Error switching company:", errors);
          toast.error(t("company_switch_error"));
        },
      },
    );
  };

  return (
    <div className="space-y-1">
      {/* Current Company Header */}
      <div className="flex items-center gap-2 px-2 py-1">
        <Building2 className="text-foreground/70 h-4 w-4" />
        <span className="text-foreground/80 text-xs font-semibold tracking-wide uppercase">
          {t("organizations")}
        </span>
      </div>

      {/* Company List */}
      <div className="space-y-1">
        {companies.map((company) => {
          const isSelected = Number(selectedCompany?.id) === Number(company.id);
          return (
            <Button
              key={String(company.id)}
              variant="ghost"
              size="sm"
              onClick={() => handleCompanyChange(company)}
              className={`h-auto w-full justify-start p-2 transition-all ${
                isSelected
                  ? "disabled:cursor-default disabled:opacity-100"
                  : "hover:bg-accent/80 hover:shadow-sm"
              }`}
              disabled={isSelected}
            >
              <div className="flex w-full items-center gap-3">
                <div className="flex-shrink-0">
                  {company.logo ? (
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={company.logo} alt={company.name} />
                      <AvatarFallback
                        className={`text-xs font-bold ${isSelected ? "text-foreground bg-primary/10" : ""}`}
                      >
                        {getInitials(company.name)}
                      </AvatarFallback>
                    </Avatar>
                  ) : (
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-all ${
                        isSelected
                          ? "text-foreground bg-primary/10"
                          : "text-foreground/70 bg-secondary hover:bg-secondary/80"
                      }`}
                    >
                      {getInitials(company.name)}
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1 text-left">
                  <div
                    className={`truncate text-sm transition-all ${isSelected ? "text-foreground font-semibold" : "font-medium"}`}
                  >
                    {company.name}
                  </div>
                </div>
                {isSelected && <Check className="h-4 w-4 flex-shrink-0 font-bold" />}
              </div>
            </Button>
          );
        })}
      </div>
    </div>
  );
}
